import type { MapModel } from '../engine/map/mapModel';
import type { HexMapState } from '../engine/render/renderer';
import { getGameConfig } from '../config/gameConfig';
import type { Rng } from './rng';
import { getCarryingCapacity, totalWildlife } from './wildlife';

type WildlifeBucket = { age: number; amount: number };

type NeighborInfo = {
    settlement: HexMapState['settlementsById'][string];
    distance: number;
    capacity: number;
    wildlife: number;
};

export function moveWildlifeProportional(
    from: { regionResources: { wild_life: WildlifeBucket[] } },
    to: { regionResources: { wild_life: WildlifeBucket[] } },
    count: number,
): boolean {
    const source = from.regionResources.wild_life;
    const total = source.reduce((sum, entry) => sum + entry.amount, 0);
    if (total <= 0) return false;
    const desired = Math.min(count, total);
    if (desired <= 0) return false;

    const allocations = source.map((entry) => {
        const expected = (entry.amount / total) * desired;
        const base = Math.floor(expected);
        return { entry, base, frac: expected - base };
    });

    let allocated = allocations.reduce((sum, entry) => sum + entry.base, 0);
    let remaining = desired - allocated;
    allocations.sort((a, b) => {
        if (b.frac !== a.frac) return b.frac - a.frac;
        return a.entry.age - b.entry.age;
    });

    for (let i = 0; i < allocations.length && remaining > 0; i += 1) {
        allocations[i].base += 1;
        remaining -= 1;
    }

    for (const allocation of allocations) {
        const move = Math.min(allocation.base, allocation.entry.amount);
        if (move <= 0) continue;
        allocation.entry.amount -= move;
        const targetEntry = to.regionResources.wild_life.find(
            (entry) => entry.age === allocation.entry.age,
        );
        if (targetEntry) targetEntry.amount += move;
        else to.regionResources.wild_life.push({ age: allocation.entry.age, amount: move });
    }

    for (let i = source.length - 1; i >= 0; i -= 1) {
        if (source[i].amount <= 0) source.splice(i, 1);
    }

    return true;
}

function pickRandom<T>(items: T[], rng: Rng): T | null {
    if (items.length === 0) return null;
    const index = Math.floor(rng() * items.length);
    return items[index] ?? null;
}

function randomIntInclusive(min: number, max: number, rng: Rng): number {
    if (max < min) return min;
    const span = max - min + 1;
    return min + Math.floor(rng() * span);
}

export function runWildlifeMigration(
    model: MapModel,
    state: HexMapState,
    rng: Rng = Math.random,
): boolean {
    const {
        migrationInterval,
        migrationRadius,
        migrationRate,
        colonizeEmptyChance,
        colonizeEmptyMin,
        colonizeEmptyMax,
    } = getGameConfig().wildLife;
    let changed = false;

    const radius = Math.max(1, Math.floor(migrationRadius));

    for (const settlement of Object.values(state.settlementsById)) {
        settlement.production.ecology.migrationTick += 1;
        if (settlement.production.ecology.migrationTick < migrationInterval) continue;
        settlement.production.ecology.migrationTick = 0;

        const { q, r, s } = settlement.tilePosition;
        const tile = model.getTileByCube(q, r, s);
        if (!tile) continue;

        const capacity = getCarryingCapacity(settlement);
        const wildlifeTotal = totalWildlife(settlement);
        if (capacity <= 0) continue;

        const neighbors: NeighborInfo[] = [];
        for (let dq = -radius; dq <= radius; dq += 1) {
            for (
                let dr = Math.max(-radius, -dq - radius);
                dr <= Math.min(radius, -dq + radius);
                dr += 1
            ) {
                if (dq === 0 && dr === 0) continue;
                const nq = tile.position.q + dq;
                const nr = tile.position.r + dr;
                const ns = -nq - nr;
                const neighborTile = model.getTileByCube(nq, nr, ns);
                if (!neighborTile) continue;
                const neighborSettlement = state.settlementByTileId[neighborTile.id];
                if (!neighborSettlement) continue;
                const neighborCapacity = getCarryingCapacity(neighborSettlement);
                const neighborWildlife = totalWildlife(neighborSettlement);
                const distance = Math.max(Math.abs(dq), Math.abs(dr), Math.abs(-dq - dr));
                neighbors.push({
                    settlement: neighborSettlement,
                    distance,
                    capacity: neighborCapacity,
                    wildlife: neighborWildlife,
                });
            }
        }

        if (wildlifeTotal === 0) {
            if (neighbors.length === 0) continue;
            if (rng() > colonizeEmptyChance) continue;
            const donors = neighbors.filter((neighbor) => neighbor.wildlife > 0);
            if (donors.length === 0) continue;
            const maxWildlife = Math.max(...donors.map((neighbor) => neighbor.wildlife));
            const donorCandidates = donors.filter((neighbor) => neighbor.wildlife === maxWildlife);
            const donor = pickRandom(donorCandidates, rng);
            if (!donor) continue;
            const count = randomIntInclusive(colonizeEmptyMin, colonizeEmptyMax, rng);
            if (count <= 0) continue;
            if (moveWildlifeProportional(donor.settlement, settlement, count)) changed = true;
            continue;
        }

        if (wildlifeTotal <= capacity) continue;
        const candidates = neighbors.filter(
            (neighbor) => neighbor.capacity > neighbor.wildlife && neighbor.capacity > 0,
        );
        if (candidates.length === 0) continue;

        const bestSpare = Math.max(
            ...candidates.map((neighbor) => neighbor.capacity - neighbor.wildlife),
        );
        const spareCandidates = candidates.filter(
            (neighbor) => neighbor.capacity - neighbor.wildlife === bestSpare,
        );
        const bestDistance = Math.min(...spareCandidates.map((neighbor) => neighbor.distance));
        const distanceCandidates = spareCandidates.filter(
            (neighbor) => neighbor.distance === bestDistance,
        );
        const target = pickRandom(distanceCandidates, rng);
        if (!target || target.settlement === settlement) continue;

        const moveCount = Math.max(1, Math.floor(wildlifeTotal * migrationRate));
        if (moveWildlifeProportional(settlement, target.settlement, moveCount)) changed = true;
    }

    return changed;
}
